import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

interface CustomerInquiry {
  id: string;
  userInfo: {
    name: string;
    email: string;
    organization: string;
  };
  category: 'general' | 'portville' | 'parking' | 'facility' | 'account';
  subject: string;
  status: 'pending' | 'answered' | 'closed';
  priority: 'low' | 'normal' | 'high';
  createdAt: Date;
}

interface RecentCustomerInquiriesProps {
  inquiries: CustomerInquiry[];
}

const RecentCustomerInquiries: React.FC<RecentCustomerInquiriesProps> = ({ inquiries }) => {
  const navigate = useNavigate();

  const getStatusColor = (status: CustomerInquiry['status']) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'answered':
        return 'text-green-600 bg-green-100';
      case 'closed':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status: CustomerInquiry['status']) => {
    switch (status) {
      case 'pending':
        return '답변대기';
      case 'answered':
        return '답변완료';
      case 'closed':
        return '문의완료';
      default:
        return status;
    }
  };

  const getCategoryText = (category: CustomerInquiry['category']) => {
    switch (category) {
      case 'general':
        return '일반';
      case 'portville':
        return '포트빌';
      case 'parking':
        return '주차';
      case 'facility':
        return '시설';
      case 'account':
        return '계정';
      default:
        return category;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">최근 고객문의</h2>
      <div className="space-y-4">
        {inquiries.length === 0 ? (
          <p className="text-gray-500 text-sm">최근 고객문의 내역이 없습니다.</p>
        ) : (
          inquiries.map((inquiry) => (
            <div
              key={inquiry.id}
              className="border-b border-gray-200 pb-4 last:border-0 p-2 rounded-lg transition-colors cursor-pointer hover:bg-gray-50"
              onClick={() => navigate(`/admin/inquiry/${inquiry.id}`)}
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0 pr-1">
                  <p className="font-medium text-gray-900 truncate">{inquiry.subject}</p>
                  <p className="text-sm text-gray-500 truncate">
                    {inquiry.userInfo.name} · {inquiry.userInfo.organization}
                  </p>
                  <p className="text-sm text-gray-500">
                    {inquiry.createdAt ? format(inquiry.createdAt, 'yyyy년 M월 d일') : '-'}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <span className="px-2 py-1 text-xs font-medium rounded-full text-gray-600 bg-gray-100">
                    {getCategoryText(inquiry.category)}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(inquiry.status)}`}>
                    {getStatusText(inquiry.status)}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentCustomerInquiries;
